import type { SVGProps } from 'react'

/*
 * The handful of Material-style glyphs the chrome needs, inlined so the app ships no icon
 * font. Every icon is a 24px viewBox filled with currentColor.
 */

type IconProps = SVGProps<SVGSVGElement>

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      // An icon carrying a label is one the page says something with, so it is left in the
      // tree to be read; an unlabelled one is decoration beside text that already says it.
      aria-hidden={props['aria-label'] === undefined}
      {...props}
    >
      {children}
    </svg>
  )
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z" />
    </Icon>
  )
}

/** The mark of a list of templates: lines on a page, each with its own bullet. */
export function ListIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 5.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm0 5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm0 5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zM9 6h11v2H9V6zm0 5h11v2H9v-2zm0 5h11v2H9v-2z" />
    </Icon>
  )
}

/** The mark of a recording on its way up: a cloud with an arrow into it. */
export function UploadIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4a7.48 7.48 0 0 0-6.63 4.03A5.99 5.99 0 0 0 6 20h13a4.99 4.99 0 0 0 .35-9.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
    </Icon>
  )
}

/** The mark of the script behind a template: angle brackets around a slash. */
export function CodeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
    </Icon>
  )
}

/** The mark of a preview: an eye over what a template leads to. */
export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5c-5 0-9.27 3.11-11 7.5C2.73 16.89 7 20 12 20s9.27-3.11 11-7.5C21.27 8.11 17 5 12 5zm0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
    </Icon>
  )
}

export function PlusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
    </Icon>
  )
}

export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </Icon>
  )
}

/** The Material help outline: a question mark in a ring. */
export function HelpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11 18h2v-2h-2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4" />
    </Icon>
  )
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </Icon>
  )
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </Icon>
  )
}

export function EditIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </Icon>
  )
}

export function DeleteIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </Icon>
  )
}

export function DownloadIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z" />
    </Icon>
  )
}

export function PlayIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8 5v14l11-7z" />
    </Icon>
  )
}

/** A flame: this set was a warm-up. */
/** A circle of arrow: the one thing left to do with something that would not load. */
export function RetryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 7.73 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </Icon>
  )
}

export function FlameIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13.5 .67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73S7.1 7.53 7.1 5.47l.03-.36A10.6 10.6 0 0 0 4.5 12a7.5 7.5 0 1 0 15 0c0-4.6-2.21-8.7-6-11.33zM11.71 19c-1.66 0-3-1.3-3-2.92 0-1.51.98-2.57 2.62-2.9s3.35-1.13 4.28-2.4c.36 1.19.55 2.44.55 3.72 0 2.48-2.01 4.5-4.45 4.5z" />
    </Icon>
  )
}

/**
 * What a set was performed to. The note glyph rather than the ♪ character: that character is a
 * hairline in every face there is and sits low in its line, so beside words of its own size it
 * reads as a speck of dirt rather than as the thing that says what the line is about.
 */
export function MusicIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3z" />
    </Icon>
  )
}

export function PauseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </Icon>
  )
}

export function SoundOnIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05A4.47 4.47 0 0 0 16.5 12zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    </Icon>
  )
}

export function SoundOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.9 8.9 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z" />
    </Icon>
  )
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
    </Icon>
  )
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" />
    </Icon>
  )
}

export function HistoryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
    </Icon>
  )
}

/** A month on a wall: the days something was done on. */
export function CalendarIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z" />
    </Icon>
  )
}

/** A cup: the best there has ever been. */
export function TrophyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94A5.01 5.01 0 0 0 11 15.9V19H7v2h10v-2h-4v-3.1a5.01 5.01 0 0 0 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
    </Icon>
  )
}

/** A ruler's ticks: the step a weight is rounded to. */
export function SnapIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h2v4h2V8h2v4h2V8h2v4h2V8h2v4h2V8h2v8z" />
    </Icon>
  )
}

export function BarbellIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20.57 14.86 22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z" />
    </Icon>
  )
}

/** The Material self-improvement figure — the mark a bodyweight exercise wears. */
export function BodyweightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="6" r="2" />
      <path d="M21 16v-2c-2.24 0-4.16-.96-5.6-2.68l-1.34-1.6A1.98 1.98 0 0 0 12.53 9h-1.05c-.59 0-1.15.26-1.53.72l-1.34 1.6C7.16 13.04 5.24 14 3 14v2c2.77 0 5.19-1.17 7-3.25V15l-3.88 1.55c-.67.27-1.12.93-1.12 1.66C5 19.2 5.8 20 6.79 20H9v-.5a2.5 2.5 0 0 1 2.5-2.5h3c.28 0 .5.22.5.5s-.22.5-.5.5h-3a1.5 1.5 0 0 0-1.5 1.5v.5h7.21c.99 0 1.79-.8 1.79-1.79 0-.73-.45-1.39-1.12-1.66L14 15v-2.25c1.81 2.08 4.23 3.25 7 3.25z" />
    </Icon>
  )
}

/** The Material timer — the mark a hold wears. */
export function TimerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15 1H9v2h6zm-4 13h2V8h-2zm8.03-6.61 1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42A8.962 8.962 0 0 0 12 4a9 9 0 1 0 9 9c0-2.12-.74-4.07-1.97-5.61zM12 20a7 7 0 1 1 0-14 7 7 0 0 1 0 14z" />
    </Icon>
  )
}

/** The Material runner — the mark a run wears. */
export function RunIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9 1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3a7.14 7.14 0 0 0 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2z" />
    </Icon>
  )
}

/** A camera: this set asks to be filmed. */
export function VideoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z" />
    </Icon>
  )
}

/** An arrow curling back: take the last change back. */
/** A square: what a thing standing does when it is stopped, rather than taken away. */
export function StopIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 6h12v12H6z" />
    </Icon>
  )
}

export function UndoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62A8.9 8.9 0 0 1 12.5 10.5c3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
    </Icon>
  )
}

/** The undo arrow mirrored: put the change back. */
export function RedoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M18.4 10.6A8.98 8.98 0 0 0 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16a6.9 6.9 0 0 1 7.6-5.5c1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" />
    </Icon>
  )
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z" />
    </Icon>
  )
}

/** A speech mark: a note rides on this. */
export function NoteIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM18 14H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
    </Icon>
  )
}

export function ArrowUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" />
    </Icon>
  )
}

export function ArrowDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z" />
    </Icon>
  )
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </Icon>
  )
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
    </Icon>
  )
}

/** Two sheets stacked: exercises taken together, as a superset is. */
export function LayersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z" />
    </Icon>
  )
}

/** Two blocks stacked: a page laid out as sections, one over the next. */
export function ViewAgendaIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 13H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h17c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zm0-10H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h17c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1z" />
    </Icon>
  )
}

/** Two arrows trading places: one thing standing in for another. */
export function SwapIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" />
    </Icon>
  )
}

/** Arrows chasing each other round: a rotation taking turns. */
export function CycleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z" />
    </Icon>
  )
}

/** A compass needle: the mark of going out to look at what other people wrote. */
export function ExploreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm2.19-11.86L7.5 10.62a1 1 0 0 0-.6.6l-2.48 6.69a.5.5 0 0 0 .64.64l6.69-2.48a1 1 0 0 0 .6-.6l2.48-6.69a.5.5 0 0 0-.64-.64zM12 13.2a1.2 1.2 0 1 1 0-2.4 1.2 1.2 0 0 1 0 2.4z" />
    </Icon>
  )
}

/** A globe: the template stops being only yours. */
export function PublishIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.92 6h-2.95a15.65 15.65 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14A7.8 7.8 0 0 1 4 12c0-.69.1-1.36.26-2h3.38a16.5 16.5 0 0 0 0 4H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 0 1 5.08 16zm2.95-8H5.08a7.99 7.99 0 0 1 4.33-3.56A15.65 15.65 0 0 0 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66a14.7 14.7 0 0 1 0-4h4.68a14.7 14.7 0 0 1 0 4zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56zM16.36 14a16.5 16.5 0 0 0 0-4h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
    </Icon>
  )
}

/** The globe struck through: it stops being everybody's. */
export function UnpublishIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2a10 10 0 0 1 8.2 15.72l-1.45-1.45c.16-.4.3-.83.4-1.27h-2.1l-2-2h4.03a14.7 14.7 0 0 0 0-4h-3.38a16.5 16.5 0 0 1 .04 3.06L13.6 8.6c.1-.2.2-.4.31-.6h-1.5l-2-2h4.02a15.65 15.65 0 0 0-1.38-3.56c.32.06.63.14.94.24A9.96 9.96 0 0 0 12 2zM3.9 2.5 21.5 20.1l-1.4 1.4-2.62-2.61A9.96 9.96 0 0 1 12 22 10 10 0 0 1 2 12c0-2.13.67-4.1 1.8-5.72L2.5 3.9zM5.08 16h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 0 1 5.08 16zm-.82-2h3.38a16.5 16.5 0 0 1-.04-3.06L5.24 9.16A7.9 7.9 0 0 0 4 12c0 .69.1 1.36.26 2zm5.83 2h3.82c-.43 1.43-1.08 2.76-1.91 3.96A15.3 15.3 0 0 1 10.09 16z" />
    </Icon>
  )
}

/** Three dots down a line: the quieter choices, kept behind one press. */
export function MoreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </Icon>
  )
}

/** Two sheets, one behind the other: the same thing again. */
export function CopyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
    </Icon>
  )
}

/** A path forking in two: what was one thing going separate ways. */
export function SplitIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14 4l2.29 2.29-2.88 2.88 1.42 1.42 2.88-2.88L20 10V4zm-4 0H4v6l2.29-2.29 4.71 4.7V20h2v-8.41l-5.29-5.3z" />
    </Icon>
  )
}

/** The mark of feedback: a speech bubble with something written in it. */
export function FeedbackIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 9h10v2H7V9zm6 5H7v-2h6v2zm4-6H7V6h10v2z" />
    </Icon>
  )
}

/** The mark of signing out: a door with an arrow leaving through it. */
export function SignOutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M17 7l-1.41 1.41L17.17 10H9v2h8.17l-1.58 1.59L17 15l4-4-4-4zM5 5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7v-2H5V5z" />
    </Icon>
  )
}

/** The mark of storage: the platters of a drive, stacked. */
export function StorageIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2C7.58 2 4 3.34 4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5c0-1.66-3.58-3-8-3zm0 2c3.87 0 6 1.1 6 1s-2.13 1-6 1-6-1.1-6-1 2.13-1 6-1zm6 15c0 .1-2.13 1-6 1s-6-.9-6-1v-2.23c1.5.76 3.72 1.23 6 1.23s4.5-.47 6-1.23V19zm0-5c0 .1-2.13 1-6 1s-6-.9-6-1v-2.23C7.5 12.53 9.72 13 12 13s4.5-.47 6-1.23V14zm-6-3c-3.87 0-6-1.1-6-1V7.77C7.5 8.53 9.72 9 12 9s4.5-.47 6-1.23V10c0 .1-2.13 1-6 1z" />
    </Icon>
  )
}

/** The mark of a weight: a scale. What units are chosen for. */
export function ScaleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3a3 3 0 0 0-2.83 2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4.17A3 3 0 0 0 12 3zm0 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0 4c2.76 0 5 2.24 5 5H7c0-2.76 2.24-5 5-5z" />
    </Icon>
  )
}

/** Steps: a pair of prints, one ahead of the other, which is what walking leaves behind. */
export function FootstepsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      {/* A sole and a heel apiece, the pair stepped diagonally so the glyph reads as walking
          rather than as standing. Primitives rather than one path: at this size the shape is
          two ovals and two stubs, and a hand-authored outline of that is only harder to keep. */}
      <g transform="rotate(-14 8 8)">
        <ellipse cx="8" cy="6.6" rx="2.7" ry="4" />
        <rect x="5.9" y="11.6" width="4.2" height="3.8" rx="1.9" />
      </g>
      <g transform="rotate(-14 16 13)">
        <ellipse cx="16" cy="11.6" rx="2.7" ry="4" />
        <rect x="13.9" y="16.6" width="4.2" height="3.8" rx="1.9" />
      </g>
    </Icon>
  )
}

/*
 * The MixedGrip mark, the paths of branding/logo.svg. It is wider than it is tall, so it is the
 * one icon here that is not a 24px square: size it by height and let the width follow.
 */
export function LogoMark(props: IconProps) {
  return (
    <svg viewBox="0 0 64 47" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M2 47L0 45V1L1 0H13L27 14V15L22 20H21L15.5 14.5L14 16V44L11 47H2Z" />
      <path d="M49 17L27 38H26L20 32V30L50 0H63L64 1V45L62 47H51L40 36V34L44 30H45L49 34L51 32V19L49 17Z" />
    </svg>
  )
}

/** The diary: a fork and a knife, which is what a meal is eaten with. */
export function DiaryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8.1 2v5.6c0 1.4-.9 2.6-2.1 3V22H4.2V10.6C3 10.2 2.1 9 2.1 7.6V2h1.5v5.6h1V2h1.5v5.6h1V2h1zM11 2c2.5 0 4.6 2.6 4.6 6.2 0 2.6-1.1 4.8-2.8 5.6V22h-1.8v-8.2C9.4 13 8.3 10.8 8.3 8.2 8.3 4.6 10.4 2 11 2zm8.8 0h1.5v20h-1.8v-7.4h-2.3V7.2C17.2 4.3 18.3 2 19.8 2z" />
    </Icon>
  )
}

/** Something being a food, wherever a food stands alone: an apple. */
/** A recipe: a book of them. */
export function RecipeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z" />
    </Icon>
  )
}

/** A meal: a knife and a fork. */
export function MealIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" />
    </Icon>
  )
}

export function ShareIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
    </Icon>
  )
}

/**
 * Sending: the paper plane, which everywhere else means handing a thing to a person — the
 * nutrition spec, Sending and sharing.
 *
 * Deliberately not the share glyph. Sharing hands over a blueprint to keep and sending hands
 * over entries to log, and the two stand side by side on a meal's page, so one mark for both
 * would be a page with two identical keys that do different things.
 */
export function SendIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
    </Icon>
  )
}

/** Saving: the floppy everybody still reads as saving. */
export function SaveIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" />
    </Icon>
  )
}

/** A clock: when something lands, or landed. */
export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
    </Icon>
  )
}

/** A figure typed instead of an amount: the two arrows that swap one for the other. */
export function SwapFigureIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 3 5 7h3v7h2V7h3L9 3zm7 14v-7h-2v7h-3l4 4 4-4h-3z" />
    </Icon>
  )
}

export function FoodIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12.6 6.8c.2-1.6 1.2-3 2.8-3.7l.6 1.4c-1 .5-1.7 1.3-1.9 2.3 1-.5 2.1-.7 3.2-.5 2.4.4 4.1 2.8 3.5 6.2-.7 4.2-3.2 8.5-6 8.5-.9 0-1.5-.4-2.4-.4s-1.6.4-2.5.4c-2.9 0-5.4-4.3-6.1-8.5C3.2 9.1 4.9 6.7 7.3 6.3c1.3-.2 2.6.1 3.6.9.6.2 1.2.2 1.7-.4z" />
    </Icon>
  )
}

/** A target, for the goal. */
export function TargetIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm0 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
    </Icon>
  )
}

/**
 * A star, filled where the thing it stands beside is one of the account's favourites and
 * outlined where it is not — the nutrition spec, Favourites.
 */
export function StarIcon({ filled = false, ...props }: IconProps & { filled?: boolean }) {
  return (
    <Icon {...props}>
      {filled ? (
        <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3z" />
      ) : (
        <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3zm0 4.6L10.5 10.7l-3.4.5 2.5 2.4-.6 3.4 3-1.6 3 1.6-.6-3.4 2.5-2.4-3.4-.5L12 7.6z" />
      )}
    </Icon>
  )
}

/** A flame with a gauge under it: the expenditure. */
export function BurnIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2s5 4.5 5 9.5a5 5 0 0 1-10 0c0-1.5.5-2.8 1.2-3.9.4 1.1 1.2 1.9 2.3 2.1C10.2 7.5 11 4.7 12 2zm-8 18h16v2H4v-2z" />
    </Icon>
  )
}

/** A face: what stands for a person whose picture is not cut yet. */
export function FaceIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM9 8.75a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm6 0a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM7.5 14h2a2.5 2.5 0 0 0 5 0h2a4.5 4.5 0 0 1-9 0z" />
    </Icon>
  )
}
